/**
 * ATL-2004 — a re-delivered Firestore batch must not apply a second time.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT
 *
 * A create writer (`saveNewSale` first) commits ONE `WriteBatch` that `set`s the new record and its
 * lines and `update`s the counters it moves with `FieldValue.increment` — `storeStock.<store>` of
 * every cart cell, the client's `totalPurchasedValue` / `bonusValue` / `punchCardValue`. Firestore's
 * client SDKs deliver a batch AT LEAST ONCE: when the write stream breaks after the server committed
 * but before its acknowledgement reached the device, the SDK sends the SAME mutations again on the
 * next stream (firebase-ios-sdk 11.15.0 `remote_store.cc:447-455` `OnWriteStreamHandshakeComplete`
 * re-sends the whole `write_pipeline_`; firebase-android-sdk `RemoteStore.java:666-673`; the web SDK
 * the same, `@firebase/firestore` 4.8.0). The batch is also persisted, so an app killed in that window
 * re-sends it at the next start. The `set`s land on the same client-minted ids and change nothing;
 * every `increment` is a server-side transform and applies AGAIN. Measured in prod (ATL-2004, archive
 * atl1991-doubleinc-20261102): 7 sales in 3 orgs, stock short by a full sale on every cell and the
 * client's total inflated by one sale — with exactly one movement row, so no ledger shows it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY NOT `create` / A TRANSACTION
 *
 * The only thing that can refuse a re-delivery on the server is a PRECONDITION, and a client
 * `WriteBatch` has exactly one: `update` is `Precondition::Exists(true)`, `set` and `delete` are
 * `None` (ios `api/write_batch.cc:38-62`, android `WriteBatch.java:91,166,180`). There is no
 * `create` / `exists: false` on a client batch — `@react-native-firebase/firestore` 22.4.0's batch
 * bridge only knows SET / UPDATE / DELETE (`RNFBFirestoreDocumentModule.m:255-278`). `exists: false`
 * exists only inside a TRANSACTION (`core/transaction.cc:131-141`), and transactions fail when the
 * device is offline, while the till is built to keep selling offline. (ATL-1382 reached the same wall.)
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE GUARD: a claim the server has already acknowledged
 *
 * A CLAIM is an empty document `writeClaims/<id>` minted by this device AHEAD of time. The batch of a
 * guarded write consumes one: `update(claim)` — its `exists: true` precondition — then `delete(claim)`.
 *  - First delivery: the claim exists, the batch applies, the claim is gone.
 *  - Any re-delivery of that batch: the claim does not exist, `update` fails `NOT_FOUND`, and a batch
 *    is atomic, so NOTHING in it applies — not one increment. `NOT_FOUND` is a permanent write error,
 *    so the SDK drops the batch and stops re-sending it (`remote/datastore.cc:386-420`,
 *    `remote_store.cc:530-552` `HandleWriteError` → `HandleRejectedWrite`).
 *
 * The claim must be ACKNOWLEDGED BEFORE it is used, and that is the whole point. A claim minted by a
 * write that is still unacknowledged could be re-sent together with the batch that consumes it — a
 * broken stream re-sends the whole unacknowledged prefix in order — and would re-create the claim
 * right before the consuming batch's re-delivery, which would then pass. (That is why ATL-1382
 * rejected a mint-then-consume pair.) A batch whose commit RESOLVED has been acknowledged by the
 * server (ios `FIRWriteBatch commitWithCompletion`, android `WriteBatch.commit()` Task, web
 * `commit()` all settle on the server's ack, never on the local write), and an acknowledged batch is
 * removed from the mutation queue (`local/local_store.cc:252-290` `AcknowledgeBatch` →
 * `RemoveMutationBatch`), so it is never sent again. Only after that are its ids added to the pool.
 *
 * So the pool is minted while the device is online and spent online OR OFFLINE: an offline sale
 * consumes a claim exactly like an online one, the SDK queues the batch, and its delivery on
 * reconnect — and every re-delivery — is decided by the server.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT A REFUSED RE-DELIVERY LOOKS LIKE TO THE WRITER
 *
 * When the first acknowledgement is lost, the writer's `commit()` promise is still pending (that is
 * how the Android cases in ATL-2004 resolved only after the second delivery). With the guard it
 * REJECTS with `not-found` instead. `commitWithRedeliveryGuard` then asks the server:
 *  - the record the batch creates EXISTS            → the first delivery applied: SUCCESS, resolve.
 *                                                     The caller's dual-write runs once, as before.
 *  - the claim still EXISTS                          → the refusal was about another document (a
 *                                                     deleted product): the original error, unchanged.
 *  - record AND claim gone                           → either the record applied and was deleted
 *                                                     since (by another till, inside the lost-ack
 *                                                     window — minutes in prod), or the claim was
 *                                                     spent elsewhere. The two cannot be told apart,
 *                                                     so nothing is retried and nothing is guessed:
 *                                                     `WriteOutcomeUnknownError`.
 *  - the server cannot be read                       → `WriteOutcomeUnknownError`.
 * A writer whose app was killed never sees the rejection: the SDK drops the refused re-delivery and
 * the first delivery stands — which is the correct outcome.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE INVARIANT: a claim is spent by at most one batch
 *
 * Two batches carrying one claim would make the second one fail — a lost write. The pool hands a
 * claim out synchronously (one JS thread) and PERSISTS its removal before the batch is built; a
 * persist that fails means the claim is not used at all.
 *
 * NEVER IN THE WAY OF A SALE (the same rule as the mirror intent): that persist is capped at
 * `CLAIM_PERSIST_CAP_MS`. Past the cap the write commits unguarded, the claim it took is burnt (it
 * stays out of the in-memory pool, and is an orphan if its removal never lands), and a session
 * CIRCUIT BREAKER trips: persists are serialised (`persistChain`), so one hung AsyncStorage write would
 * make every later claim wait the full cap. After the trip `take()` hands out nothing, at zero added
 * latency, for the rest of the session (`write_claim_persist_timeout`, then `write_claim_pool_empty`
 * with reason `persist-tripped`). The persisted pool is keyed by this
 * install's id (`installIdFromIdentity`, the outbox v2 derivation: vendor id + installation time),
 * so a pool restored onto another device or into a reinstall is never read. The one hole is the
 * same one the outbox documents: an iOS erase-and-restore onto the SAME device may reproduce the id
 * and restore a pool whose claims were spent after the backup. A sale that spends such a claim
 * online is rejected while its promise is alive and surfaces as `WriteOutcomeUnknownError`; one
 * queued offline and then killed before reconnecting would be dropped. On web the pool is
 * memory-only (one per tab), because Firestore on web runs without persistence here anyway.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IS NOT COVERED
 *  - A write made while the pool is EMPTY is committed unguarded, exactly as before, and counted
 *    (`write_claim_pool_empty`). That happens after a cold start that never reached the server, or
 *    when an offline stretch outlasts the pool (`POOL_TARGET`: 60 writes on native).
 *  - Only CREATE writers can be guarded: "applied" is decided by the created record's existence.
 *  - The organization must be on Remote Config `write_claim_org_allowlist` ("*" or a JSON array of
 *    org firestore ids; unset / malformed → nobody). It ships DARK.
 *
 * Claims live in a ROOT collection on purpose: org subcollections are enumerated by the migration's
 * completeness census (`listCollections()`), and a claim is not books data.
 */
import * as Sentry from '@sentry/react-native'

import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import { isStorageSafeId } from '../Write/outboxEntry'
import { parseMirrorIntentAllowlist } from './mirrorIntent'
import { isFirestoreNotFound } from './firestoreNotFound'
import { WRITE_OUTCOME_UNKNOWN_CODE } from './writeOutcome'

/* eslint-disable @typescript-eslint/no-explicit-any -- Firestore refs/batches are untyped across both SDKs here, as in every writer module */

export const WRITE_CLAIMS_COLLECTION = 'writeClaims'
export const WRITE_CLAIM_ALLOWLIST_KEY = 'write_claim_org_allowlist'
/**
 * How many acknowledged claims a pool keeps: the number of guarded writes it covers with no network.
 * Native keeps a durable pool; web's is per tab and lost on reload (its unspent claims are orphans).
 */
export const POOL_TARGET = { native: 60, web: 10 } as const
/** How long a post-refusal server read may take before the outcome is `unknown`. */
export const OUTCOME_PROBE_TIMEOUT_MS = 10_000
/**
 * How long a sale may wait for the claim's removal to become durable before it commits unguarded.
 * `mirrorIntent.PERSIST_CAP_MS` (400 ms against a measured 2–6 ms happy path): Android AsyncStorage
 * can hang, and the guard is never allowed to hold a sale up.
 */
export const CLAIM_PERSIST_CAP_MS = 400
const STORAGE_KEY_PREFIX = 'atlas.writeClaims.v1'

/** The outcome of a guarded write could not be established; the record may or may not exist. */
export class WriteOutcomeUnknownError extends Error {
  readonly code = WRITE_OUTCOME_UNKNOWN_CODE
  constructor(
    readonly writer: string,
    readonly documentPath: string,
    readonly reason: 'unreadable' | 'record-and-claim-gone',
    readonly cause: unknown,
  ) {
    super(
      `${writer}: Firestore refused a re-delivery of ${documentPath} and its outcome could not be established (${reason})`,
    )
    this.name = 'WriteOutcomeUnknownError'
  }
}

// ============================================================================
// The pool
// ============================================================================

export interface ClaimStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
}

/** `durable`: the claim may be used. `failed` / `timeout`: commit without it (module docblock). */
export type ClaimPersistOutcome = 'durable' | 'failed' | 'timeout'

export interface WriteClaim {
  readonly id: string
  readonly ref: any
  /** How the claim's removal from the persisted pool settled, within `CLAIM_PERSIST_CAP_MS`. Never rejects. */
  readonly persisted: Promise<ClaimPersistOutcome>
}

export interface WriteClaimDeps {
  /** The root Firestore (`rootDb`): `batch()` and `collection()`. */
  db: { batch: () => any; collection: (name: string) => { doc: (id?: string) => any } }
  serverTimestamp: () => unknown
  /** Raw `write_claim_org_allowlist`, synchronous. */
  allowlist: () => string
  /** Durable storage for the pool, or null for a memory-only pool (web). */
  storage: ClaimStorage | null
  /** This install's id; null = no durable identity, so the pool stays memory-only. */
  installId: () => Promise<string | null>
  platform: string
  /** Pool size; a refill starts when the pool falls below half of it. */
  target: number
  /** `CLAIM_PERSIST_CAP_MS` unless a test shortens it. */
  persistCapMs?: number
}

interface OrgPool {
  ids: string[]
  /** null until the persisted pool was read (or found not to exist). */
  loaded: boolean
  loading: Promise<void> | null
  minting: Promise<void> | null
  storageKey: string | null
  persistChain: Promise<unknown>
}

export interface WriteClaimGuard {
  /** Is the guard on for this organization (Remote Config)? Synchronous, never throws. */
  active(orgFirestoreId: string | undefined): boolean
  /** Read the persisted pool and top it up. Never rejects. */
  start(orgFirestoreId: string): Promise<void>
  /** Hand out one acknowledged claim, or null (inactive, not loaded, empty). Synchronous. */
  take(orgFirestoreId: string | undefined, writer: string): WriteClaim | null
  /** Start a refill when the pool is low. Never rejects. */
  refill(orgFirestoreId: string): Promise<void>
  /** Claims available now (tests and telemetry). */
  size(orgFirestoreId: string): number
}

export function createWriteClaimGuard(deps: WriteClaimDeps): WriteClaimGuard {
  const target = deps.target
  const refillBelow = Math.ceil(target / 2)
  const persistCapMs = deps.persistCapMs ?? CLAIM_PERSIST_CAP_MS
  const pools = new Map<string, OrgPool>()
  /** The session circuit breaker (module docblock): once a claim's persist overran the cap. */
  let persistTripped = false

  /** `persist(p)` for a claim being handed out, capped; trips the breaker on the first overrun. */
  const persistClaim = (p: OrgPool, writer: string): Promise<ClaimPersistOutcome> => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const capped = new Promise<ClaimPersistOutcome>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), persistCapMs)
    })
    const settled = persist(p).then((durable): ClaimPersistOutcome => (durable ? 'durable' : 'failed'))
    return Promise.race([settled, capped]).then((outcome) => {
      clearTimeout(timer)
      if (outcome === 'timeout' && !persistTripped) {
        persistTripped = true
        captureOperationalEvent('write_claim_persist_timeout', { writer, platform: deps.platform, capMs: persistCapMs })
      }
      return outcome
    })
  }

  const pool = (org: string): OrgPool => {
    let p = pools.get(org)
    if (!p) {
      p = { ids: [], loaded: false, loading: null, minting: null, storageKey: null, persistChain: Promise.resolve() }
      pools.set(org, p)
    }
    return p
  }

  const active = (org: string | undefined): boolean => {
    try {
      if (!org) return false
      const list = parseMirrorIntentAllowlist(deps.allowlist())
      return list.all || list.ids.has(org)
    } catch {
      return false
    }
  }

  /** Write the pool's current ids; resolves false when they could not be made durable. */
  const persist = (p: OrgPool): Promise<boolean> => {
    if (!deps.storage || !p.storageKey) return Promise.resolve(true)
    const storage = deps.storage
    const key = p.storageKey
    const run = p.persistChain.then(() => storage.setItem(key, JSON.stringify(p.ids)))
    p.persistChain = run.catch(() => undefined)
    return run.then(
      () => true,
      () => false,
    )
  }

  const load = (org: string): Promise<void> => {
    const p = pool(org)
    if (p.loaded) return Promise.resolve()
    if (p.loading) return p.loading
    p.loading = (async () => {
      try {
        const installId = deps.storage ? await deps.installId() : null
        if (deps.storage && installId && isStorageSafeId(installId) && isStorageSafeId(org)) {
          p.storageKey = `${STORAGE_KEY_PREFIX}.${installId}.${org}`
          const raw = await deps.storage.getItem(p.storageKey)
          const parsed: unknown = raw ? JSON.parse(raw) : []
          if (Array.isArray(parsed)) {
            for (const id of parsed) if (isStorageSafeId(id) && !p.ids.includes(id)) p.ids.push(id)
          }
        }
      } catch (error) {
        // An unreadable pool is not used: the claims in it may have been spent. Start a fresh one.
        captureOperationalEvent('write_claim_pool_unreadable', { platform: deps.platform, error: String(error).slice(0, 120) })
      } finally {
        p.loaded = true
        p.loading = null
      }
    })()
    return p.loading
  }

  const refill = (org: string): Promise<void> => {
    try {
      if (!active(org)) return Promise.resolve()
      const p = pool(org)
      if (!p.loaded || p.minting || p.ids.length >= refillBelow) return p.minting ?? Promise.resolve()
      const n = target - p.ids.length
      const batch = deps.db.batch()
      const minted: string[] = []
      for (let i = 0; i < n; i++) {
        const ref = deps.db.collection(WRITE_CLAIMS_COLLECTION).doc()
        minted.push(ref.id)
        batch.set(ref, { organizationId: org, platform: deps.platform, createdAt: deps.serverTimestamp() })
      }
      // The ids join the pool ONLY once the server acknowledged the batch (module docblock): an
      // unacknowledged mint could be re-sent after the claim was spent and re-open the guard.
      p.minting = Promise.resolve(batch.commit())
        .then(async () => {
          for (const id of minted) if (!p.ids.includes(id)) p.ids.push(id)
          await persist(p)
        })
        .catch((error: unknown) => {
          captureOperationalEvent('write_claim_mint_failed', { platform: deps.platform, error: String(error).slice(0, 120) })
        })
        .finally(() => {
          p.minting = null
        })
      return p.minting
    } catch (error) {
      captureOperationalEvent('write_claim_mint_failed', { platform: deps.platform, error: String(error).slice(0, 120) })
      return Promise.resolve()
    }
  }

  return {
    active,
    start: async (org: string) => {
      try {
        if (!active(org)) return
        await load(org)
        await refill(org)
      } catch {
        // Never rejects: the guard is an addition to the write path, never a way to break it.
      }
    },
    take: (org: string | undefined, writer: string): WriteClaim | null => {
      try {
        if (!org || !active(org)) return null
        if (persistTripped) {
          captureOperationalEvent('write_claim_pool_empty', { writer, platform: deps.platform, reason: 'persist-tripped' })
          return null
        }
        const p = pool(org)
        if (!p.loaded) {
          void load(org).then(() => refill(org))
          captureOperationalEvent('write_claim_pool_empty', { writer, platform: deps.platform, reason: 'not-loaded' })
          return null
        }
        const id = p.ids.shift()
        void refill(org)
        if (!id) {
          captureOperationalEvent('write_claim_pool_empty', { writer, platform: deps.platform, reason: 'empty' })
          return null
        }
        return { id, ref: deps.db.collection(WRITE_CLAIMS_COLLECTION).doc(id), persisted: persistClaim(p, writer) }
      } catch {
        return null
      }
    },
    refill,
    size: (org: string) => pools.get(org)?.ids.length ?? 0,
  }
}

// ============================================================================
// The guarded commit
// ============================================================================

type ServerReadable = { get: (options: { source: 'server' }) => Promise<unknown> }
const TIMED_OUT = Symbol('timed-out')

/** true = exists, false = does not, null = the server did not answer. */
/** `existsOnServer`'s answer as a telemetry kind. */
function probeAnswer(exists: boolean | null): 'yes' | 'no' | 'unreadable' {
  return exists === true ? 'yes' : exists === false ? 'no' : 'unreadable'
}

async function existsOnServer(ref: ServerReadable, timeoutMs: number): Promise<boolean | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const answer = await Promise.race([
      ref.get({ source: 'server' }),
      new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs)
      }),
    ])
    if (answer === TIMED_OUT || !answer) return null
    // RNFirebase exposes `exists` as a METHOD, the web compat SDK as a boolean (ATL-570).
    const exists = (answer as { exists?: unknown }).exists
    return typeof exists === 'function' ? Boolean((exists as () => unknown).call(answer)) : Boolean(exists)
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

export interface GuardedCommitSpec {
  /** The writer's label, e.g. `sales.create`. */
  writer: string
  orgFirestoreId: string | undefined
  /** The document this batch CREATES; its existence is what "the batch applied" means. */
  primaryRef: any
  /** The writer's real `WriteBatch`, fully built. The claim's two writes are appended to it last. */
  batch: any
  serverTimestamp: () => unknown
}

/**
 * Commit `batch` so that a re-delivery of it cannot apply (module docblock).
 *
 * With no claim (guard off, pool empty) this returns the batch's own `commit()` promise, so an
 * unguarded write is the exact code path it was, microtask for microtask, call for call.
 *
 * Nothing is ever retried here: the only refusal that is not an answer ("record and claim both
 * gone") is also what a sale deleted by another till inside the lost-ack window looks like, and a
 * retry would resurrect it.
 */
export function commitWithRedeliveryGuard(guard: WriteClaimGuard, spec: GuardedCommitSpec): Promise<void> {
  const claim = guard.take(spec.orgFirestoreId, spec.writer)
  if (!claim) return spec.batch.commit()
  return commitClaimed(spec, claim)
}

/**
 * Append the claim's two writes to the writer's batch, last. `update` carries the `exists: true`
 * precondition that refuses a re-delivery (the claim is gone after the first apply); `delete` spends
 * the claim in the same atomic commit.
 */
function spendClaimInBatch(batch: GuardedCommitSpec['batch'], claimRef: unknown, documentPath: string, consumedAt: unknown): void {
  batch.update(claimRef, { consumedBy: documentPath, consumedAt })
  batch.delete(claimRef)
}

async function commitClaimed(spec: GuardedCommitSpec, claim: WriteClaim): Promise<void> {
  const persisted = await claim.persisted
  if (persisted !== 'durable') {
    // The claim's removal is not durable (failed), or not within the cap (timeout, already counted
    // and the breaker tripped): after a restart it could be handed out again. Not used; commit as before.
    if (persisted === 'failed') captureOperationalEvent('write_claim_pool_empty', { writer: spec.writer, reason: 'persist-failed' })
    return spec.batch.commit()
  }
  const documentPath = String(spec.primaryRef?.path ?? spec.primaryRef?.id ?? '')
  spendClaimInBatch(spec.batch, claim.ref, documentPath, spec.serverTimestamp())
  try {
    await spec.batch.commit()
  } catch (error) {
    if (!isFirestoreNotFound(error)) throw error
    const [recordExists, claimExists] = await Promise.all([
      existsOnServer(spec.primaryRef, OUTCOME_PROBE_TIMEOUT_MS),
      existsOnServer(claim.ref, OUTCOME_PROBE_TIMEOUT_MS),
    ])
    if (recordExists === true) {
      // The first delivery applied and its acknowledgement was lost; the re-delivery was refused.
      captureOperationalEvent('write_claim_redelivery_refused', { writer: spec.writer })
      Sentry.addBreadcrumb({ category: 'write-claim', level: 'info', message: `${spec.writer}: re-delivery refused, first delivery stands` })
      return
    }
    if (claimExists === true) throw error // refused for another document; the writer explains it
    const unknown = new WriteOutcomeUnknownError(
      spec.writer,
      documentPath,
      recordExists === false && claimExists === false ? 'record-and-claim-gone' : 'unreadable',
      error,
    )
    // Counted in PostHog as well: this is the one outcome the cashier is shown ("check the list
    // before ringing it up again"), so it is the rollback signal for `write_claim_org_allowlist`,
    // and Sentry has accepted no event since 2026-09-10 (operationalTelemetry.ts). Kinds only — no
    // document path or claim id leaves the device here.
    captureOperationalEvent('write_claim_outcome_unknown', {
      writer: spec.writer,
      reason: unknown.reason,
      recordExists: probeAnswer(recordExists),
      claimExists: probeAnswer(claimExists),
    })
    Sentry.captureException(unknown, {
      level: 'error',
      tags: { write_claim_outcome_unknown: true, writer: spec.writer },
      fingerprint: ['write-claim-outcome-unknown', unknown.reason],
      contexts: { write_claim: { documentPath, claimId: claim.id, recordExists, claimExists } },
    })
    throw unknown
  }
}

// ============================================================================
// The app singleton
// ============================================================================

let appGuard: WriteClaimGuard | null = null

/**
 * `Platform.OS`, lazily: a module-level `react-native` import breaks every writer test that mocks only
 * part of it. An unknown platform is treated as web (memory-only pool).
 */
function platformOS(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return (require('react-native') as { Platform: { OS: string } }).Platform.OS
  } catch {
    return 'unknown'
  }
}

/** The guard a writer gets when the real one cannot be built: no claim, today's commit exactly. */
const INERT_GUARD: WriteClaimGuard = {
  active: () => false,
  start: () => Promise.resolve(),
  take: () => null,
  refill: () => Promise.resolve(),
  size: () => 0,
}

/**
 * Build the app's guard. Called ONCE, by `startWriteClaimPool` at app start, never from a writer: the
 * guard needs the platform Firebase module (`./firebaseFirestore`), and a writer that loaded it on its
 * first create would boot Firebase inside every writer test that calls a create — on web, resolving the
 * real `firebaseConfig` — and would pay a throw on every create where it cannot load.
 */
function buildAppWriteClaimGuard(): WriteClaimGuard {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const firestore = require('./firebaseFirestore') as {
    rootDb: WriteClaimDeps['db']
    fieldValue: { serverTimestamp: () => unknown }
    getRemoteConfigValue: (key: string) => string
  }
  const os = platformOS()
  const native = os === 'ios' || os === 'android'
  return createWriteClaimGuard({
    db: firestore.rootDb,
    serverTimestamp: () => firestore.fieldValue.serverTimestamp(),
    allowlist: () => firestore.getRemoteConfigValue(WRITE_CLAIM_ALLOWLIST_KEY),
    storage: native
      ? // eslint-disable-next-line @typescript-eslint/no-var-requires
        ((require('@react-native-async-storage/async-storage') as { default: ClaimStorage }).default)
      : null,
    installId: async () => {
      if (!native) return null
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const store = require('../Write/store.native') as {
        platformVendorId: () => Promise<string | null>
        platformInstallationTime: () => Promise<number | null>
        installIdFromIdentity: (vendorId: string, installationTimeMs: number) => string
      }
      const [vendorId, installedAt] = await Promise.all([store.platformVendorId(), store.platformInstallationTime()])
      return vendorId && installedAt ? store.installIdFromIdentity(vendorId, installedAt) : null
    },
    platform: os,
    target: native ? POOL_TARGET.native : POOL_TARGET.web,
  })
}

/**
 * The app's guard: the one `startWriteClaimPool` built, or INERT (no claim, the write commits exactly
 * as before) until it has, or when it could not be built. Never throws and never loads Firebase.
 */
export function writeClaimGuard(): WriteClaimGuard {
  return appGuard ?? INERT_GUARD
}

/**
 * `commitWithRedeliveryGuard` with the app's guard, for a writer whose batch CREATES `primaryRef`
 * (a fresh client-minted id). Only a create can use the guard: "the record exists" is how a refused
 * re-delivery is told apart from a refusal for another reason, and an edited record already existed.
 *
 * `db` is the organization handle the writer was given (`organizations/<firestoreId>`), whose id
 * selects the organization's pool and its allowlist entry.
 */
export function commitGuardedCreate(writer: string, db: unknown, primaryRef: unknown, batch: unknown): Promise<void> {
  const orgId = (db as { id?: unknown } | null | undefined)?.id
  return commitWithRedeliveryGuard(writeClaimGuard(), {
    writer,
    orgFirestoreId: typeof orgId === 'string' ? orgId : undefined,
    primaryRef,
    batch,
    // Reached only when a claim is attached; required lazily for the same reason as the guard itself.
    serverTimestamp: () =>
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      (require('./firebaseFirestore') as { fieldValue: { serverTimestamp: () => unknown } }).fieldValue.serverTimestamp(),
  })
}

let started = false
const unsubscribes: Array<() => void> = []

/**
 * Load and top up the session organization's pool at app start and whenever the session's
 * organization changes. Never throws.
 */
export function startWriteClaimPool(): void {
  if (started) return
  started = true
  try {
    appGuard = buildAppWriteClaimGuard()
  } catch (error) {
    // Every create then commits unguarded, exactly as before the guard existed — counted, not silent.
    captureOperationalEvent('write_claim_guard_unavailable', { error: String(error).slice(0, 120) })
    return
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { sessionWriteOrgId } = require('./mirrorIntent') as { sessionWriteOrgId: () => string | undefined }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getJotaiStore } = require('./types') as {
      getJotaiStore: () => { sub: (atom: unknown, listener: () => void) => () => void }
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { organizationAtom, dbAtom } = require('../AtomsProvider') as { organizationAtom: unknown; dbAtom: unknown }
    let lastOrgId: string | undefined
    // Runs inside jotai subscription callbacks too: it must never throw into the store's notify.
    const onIdentity = (): void => {
      try {
        const orgId = sessionWriteOrgId()
        if (!orgId || orgId === lastOrgId) return
        lastOrgId = orgId
        void writeClaimGuard().start(orgId)
      } catch (error) {
        captureOperationalEvent('write_claim_pool_start_failed', { error: String(error).slice(0, 120) })
      }
    }
    onIdentity()
    // The allowlist is Remote Config: at app start it is usually not fetched yet, so the first pass
    // above is a no-op for an allowlisted org. Ask again once the boot fetch settles.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { remoteConfigReady } = require('./firebaseFirestore') as { remoteConfigReady?: Promise<unknown> }
    void Promise.resolve(remoteConfigReady)
      .then(() => {
        lastOrgId = undefined
        onIdentity()
      })
      .catch(() => undefined)
    const store = getJotaiStore()
    unsubscribes.push(store.sub(organizationAtom, onIdentity), store.sub(dbAtom, onIdentity))
  } catch (error) {
    captureOperationalEvent('write_claim_pool_start_failed', { error: String(error).slice(0, 120) })
  }
}

/** TEST-ONLY. */
export function resetWriteClaimsForTests(guard: WriteClaimGuard | null = null): void {
  appGuard = guard
  started = false
  for (const unsubscribe of unsubscribes.splice(0)) {
    try {
      unsubscribe()
    } catch {
      // Nothing to do.
    }
  }
}
